<script lang="tsx">
  import { useId, type PropType } from "vue";
  import { Button, FloatLabel, InputText, Message, Password } from "primevue";
  import { Form, type FormResolverOptions, type FormSubmitEvent } from "@primevue/forms";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
  import type { LoginResult } from "@/lib/craft";


  export default {
    components: {
      Form, Button, FloatLabel, InputText, Password, Icon, Message
    },
    setup() {
      return { id: useId() };
    },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      }
    },
    data(props) { return {
      faArrowRight, faCircleNotch,
      submitting: false,
      submitError: undefined as string | undefined,
    }},
    methods: {
      resolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = { username: [], password: [] };

        if (values.username == null || values.username === "") {
          errors.username.push({ message: "Email address or username is required" });
        }
        if (values.password == null || values.password === "") {
          errors.password.push({ message: "Password is required" });
        }

        return { errors };
      },

      async submit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }
        let result: LoginResult;
        try {
          this.submitting = true;
          result = await this.$session.login(
            submit.states.username.value,
            submit.states.password.value
          );
        } finally {
          this.submitting = false;
        }
        if (result.errorCode !== undefined) {
          this.submitError = result.message;
        } else if (result.returnUrl !== undefined) {
          this.submitting = true;
          this.submitError = undefined;
          location.href = result.returnUrl;
        }
      },
    },
  };
</script>

<template>
  <Form v-slot="form" class="form" :initial-values="{username: null, password: null}"
    validate-on-value-update validate-on-submit validate-on-blur
    :resolver="resolve" @submit="submit">
    <h3>Member Login</h3>

    <div>
      <FloatLabel variant="on">
        <InputText :id="`${id}-username`" name="username" fluid required
          autocomplete="username"/>
        <label :for="`${id}-username`">
          Email Address or Username<!--
          --><span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.username?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.username.error.message }}
      </Message>
    </div>

    <div>
      <FloatLabel variant="on">
        <Password :id="`${id}-password`" name="password" fluid required
          toggleMask :feedback="false" autocomplete="password"/>
        <label :for="`${id}-password`">
          Password<!--
          --><span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.password?.errors?.length ?? 0) > 0"
        severity="error" size="small" variant="simple">
        {{ form.password.error.message }}
      </Message>
    </div>

    <Message v-if="submitError !== undefined"
      severity="error" size="small" variant="simple">
      {{ submitError }}
    </Message>

    <Button type="submit" label="Login" icon-pos="right"
      formnovalidate :disabled="!form.valid || submitting">
      <template #icon="slot">
        <Icon :class="[slot.class]" :icon="submitting ? faCircleNotch : faArrowRight" :spin="submitting"/>
      </template>
    </Button>

    <div class="extra-links">
      <a href="/en-ca/account/reset-password">Forgot password?</a>
      <a href="/en-ca/membership">Become a member</a>
    </div>
  </Form>
</template>

<style lang="less" scoped>
  h3 { text-align: center; }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-inline);
    justify-content: center;
    align-content: center;

    max-width: 35em;
    margin: 0 auto;
  }

  .extra-links {
    display: inline-flex;
    flex-direction: row;
    * {
      flex: 1 0;
      text-align: center;
    }
  }

  .required-star::before {
    content: "*";
    color: var(--p-floatlabel-invalid-color);
  }
</style>
